import { useEffect, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export interface EditableTextProps {
  /** Current persisted value. */
  value: string | null;
  /**
   * Called when the user saves, in uncontrolled (self-managed) mode. Not used
   * when `editing` is externally controlled -- the parent owns the save.
   */
  onSave?: (next: string) => Promise<unknown> | void;
  /** Called with each keystroke when `editing` is externally controlled. */
  onChange?: (next: string) => void;
  label?: ReactNode;
  multiline?: boolean;
  rows?: number;
  /** Copy shown (italic) in the read-only view when the value is empty. */
  emptyText?: string;
  /** Optional success toast copy shown after an uncontrolled save. */
  savedMessage?: string;
  /** Applied to the read-only view so long bodies scroll instead of pushing the page. */
  maxHeightClass?: string;
  size?: "default" | "sm";
  /** Read-only presentation: no Edit affordance, never enters edit mode. */
  readOnly?: boolean;
  /**
   * Externally controlled edit state. When set, this component renders no
   * Edit/Save/Cancel actions of its own -- the parent drives `editing` and
   * reads changes via `onChange`, then saves and flips it back off itself.
   */
  editing?: boolean;
  ariaLabel?: string;
  testId?: string;
  className?: string;
}

/**
 * Visual-fidelity port of the signal-v3 prototype's design-system `EditableField`:
 * a read-only tinted card that flips to an Edit / Save / Cancel textarea. Wired
 * directly to a caller-supplied `onSave` (a real mutation) rather than the
 * prototype's in-memory store.
 */
export function EditableText({
  value,
  onSave,
  onChange,
  label,
  multiline = true,
  rows = 8,
  emptyText,
  savedMessage,
  maxHeightClass,
  size = "default",
  readOnly = false,
  editing,
  ariaLabel,
  testId,
  className,
}: EditableTextProps) {
  const controlled = editing !== undefined;
  const [innerEditing, setInnerEditing] = useState(false);
  const isEditing = controlled ? editing : innerEditing;
  const [draft, setDraft] = useState(value ?? "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!controlled && !innerEditing) setDraft(value ?? "");
  }, [value, controlled, innerEditing]);

  const startEdit = () => {
    setDraft(value ?? "");
    setInnerEditing(true);
  };

  const cancelEdit = () => {
    setDraft(value ?? "");
    setInnerEditing(false);
  };

  const save = async () => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      await onSave(draft);
      setInnerEditing(false);
      if (savedMessage) toast.success(savedMessage);
    } catch {
      // Stays in edit mode -- the mutation's onError already surfaced the error.
    } finally {
      setIsSaving(false);
    }
  };

  const text = value || emptyText || "Nothing added yet.";

  const editActions = (
    <div className="flex items-center gap-2 shrink-0">
      <Button variant="outline" size="sm" onClick={cancelEdit} disabled={isSaving}>
        Cancel
      </Button>
      <Button size="sm" onClick={save} disabled={isSaving}>
        {isSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
        Save
      </Button>
    </div>
  );

  const editButton = (
    <Button
      size="sm"
      variant="ghost"
      className="shrink-0"
      onClick={startEdit}
      aria-label={ariaLabel ? `Edit ${ariaLabel}` : undefined}
    >
      <Pencil className="h-3.5 w-3.5" />
      Edit
    </Button>
  );

  const showOwnActions = !readOnly && !controlled;

  return (
    <div className={cn("space-y-1.5", className)} data-testid={testId}>
      {label || showOwnActions ? (
        <div className={cn("flex items-end gap-3", label ? "justify-between" : "justify-end")}>
          {label ? <Label className="text-sm font-medium text-muted-foreground">{label}</Label> : null}
          {showOwnActions ? (isEditing ? editActions : editButton) : null}
        </div>
      ) : null}

      {isEditing && !readOnly ? (
        multiline ? (
          <Textarea
            aria-label={ariaLabel}
            value={controlled ? value ?? "" : draft}
            onChange={(e) => {
              if (controlled) {
                onChange?.(e.target.value);
              } else {
                setDraft(e.target.value);
              }
            }}
            disabled={isSaving}
            rows={rows}
            className="rounded-lg bg-card text-sm leading-relaxed whitespace-pre-wrap resize-none"
          />
        ) : (
          <Input
            aria-label={ariaLabel}
            value={controlled ? value ?? "" : draft}
            onChange={(e) => {
              if (controlled) {
                onChange?.(e.target.value);
              } else {
                setDraft(e.target.value);
              }
            }}
            disabled={isSaving}
            className="bg-card text-sm"
          />
        )
      ) : (
        <div
          className={cn(
            "rounded-lg border border-surface-tint-subtle bg-surface-tint-subtle overflow-auto",
            size === "sm" ? "p-3" : "p-4",
            maxHeightClass,
          )}
        >
          <div
            className={cn(
              "space-y-3 text-sm leading-relaxed text-muted-foreground",
              value ? "" : "italic",
            )}
          >
            {text.split(/\n{2,}/).map((block, i) => (
              <p key={i} className="whitespace-pre-line break-words m-0">
                {block}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
